import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { InvitationInfo } from '@ste/shared';
import { ApiRequestError, useAuth, type TokenResponse } from '../../api/auth.ts';
import { API_ROOT } from '../../api/connection.ts';
import { fetchInvitation } from '../../api/sites.ts';
import { Banner, Button, Spinner } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import logo from '../../theme/assets/logo.svg?raw';
import { TotpSetupForm } from '../auth/TotpSetup.tsx';

const inputClass =
  'mt-1 block min-h-12 w-full rounded-xl border border-border bg-surface-2 px-4 text-base text-text focus:border-accent focus:outline-none';

/** accept an invitation: new customers choose name and password; existing accounts log in first */
export function InvitationPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const status = useAuth((s) => s.status);
  const [info, setInfo] = useState<InvitationInfo>();
  const [error, setError] = useState<string>();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [setupToken, setSetupToken] = useState<string>();

  useEffect(() => {
    fetchInvitation(token).then(setInfo, (e: Error) => setError(e.message));
  }, [token]);

  async function accept(event?: FormEvent) {
    event?.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const accessToken = status === 'authenticated' ? await useAuth.getState().getAccessToken() : undefined;
      const res = await fetch(`${API_ROOT}/invitations/accept`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ token, name: name || undefined, password: password || undefined }),
      });
      const body = (await res.json()) as TokenResponse & { message?: string; error?: string };
      const failure = res.ok ? undefined : new ApiRequestError(res.status, body as never);
      // installers set up two-factor login before their first session
      if (failure?.code === 'totp_setup_required' && failure.setupToken) {
        setSetupToken(failure.setupToken);
        return;
      }
      if (failure) throw failure;
      // the response signs in the new (or already logged-in) account
      useAuth.getState().adopt(body);
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div
          className="mx-auto text-text"
          style={{ width: 200 }}
          dangerouslySetInnerHTML={{ __html: logo }}
        />
        {setupToken && (
          <TotpSetupForm
            setupToken={setupToken}
            onCancel={() => navigate('/anmelden', { replace: true })}
            onDone={() => navigate('/', { replace: true })}
          />
        )}
        {!setupToken && !info && !error && <Spinner label={t('common.loading')} />}
        {error && <Banner tone="danger" title={error} />}
        {info && !setupToken && (
          <form onSubmit={accept} className="space-y-4">
            <h1 className="text-xl font-semibold">{t('invite.title')}</h1>
            <p className="text-muted">
              {t('invite.text', { site: info.siteTitle, role: t(`sites.role.${info.role}`) })}
            </p>
            <p className="text-sm text-muted">{info.email}</p>
            {info.accountExists && status !== 'authenticated' ? (
              <p className="text-sm">
                {t('invite.existing')}{' '}
                <Link className="text-accent-text underline" to="/anmelden">
                  {t('auth.submit')}
                </Link>
              </p>
            ) : info.accountExists ? null : (
              <>
                <label className="block">
                  <span className="text-sm text-muted">{t('invite.name')}</span>
                  <input
                    className={inputClass}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-muted">{t('invite.password')}</span>
                  <input
                    type="password"
                    className={inputClass}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </label>
              </>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={
                busy || (info.accountExists ? status !== 'authenticated' : !name || password.length < 10)
              }
            >
              {t('invite.accept')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
